import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface Notification {
  id: string;
  user_id: string | null;
  title: string;
  message: string;
  type: string;
  is_read: boolean;
  is_broadcast: boolean;
  created_at: string;
}

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setIsLoading(false);
      return;
    }

    try {
      // Fetch user's personal notifications
      const { data: personalNotifs, error: personalError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (personalError) throw personalError;

      // Fetch broadcast notifications
      const { data: broadcastNotifs, error: broadcastError } = await supabase
        .from('notifications')
        .select('*')
        .eq('is_broadcast', true)
        .order('created_at', { ascending: false })
        .limit(20);

      if (broadcastError) throw broadcastError;

      // Fetch which broadcasts the user has read
      const { data: readBroadcasts } = await supabase
        .from('notification_reads')
        .select('notification_id')
        .eq('user_id', user.id);

      const readBroadcastIds = new Set(readBroadcasts?.map(r => r.notification_id) || []);

      // Mark broadcasts as read if user has read them
      const markedBroadcasts = (broadcastNotifs || []).map(n => ({
        ...n,
        is_read: readBroadcastIds.has(n.id)
      }));

      // Combine and deduplicate
      const allNotifs = [...(personalNotifs || []), ...markedBroadcasts];
      const uniqueNotifs = allNotifs.reduce((acc, notif) => {
        if (!acc.find(n => n.id === notif.id)) {
          acc.push(notif);
        }
        return acc;
      }, [] as Notification[]);

      // Sort by date
      uniqueNotifs.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setNotifications(uniqueNotifs);
      setUnreadCount(uniqueNotifs.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = useCallback(async (notificationId: string) => {
    if (!user) return;

    const notification = notifications.find(n => n.id === notificationId);
    if (!notification || notification.is_read) return;

    if (notification.is_broadcast) {
      // Insert into notification_reads for broadcasts
      await supabase
        .from('notification_reads')
        .insert({ user_id: user.id, notification_id: notificationId });
    } else {
      // Update the notification directly
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);
    }

    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, [user, notifications]);

  const markAllAsRead = useCallback(async () => {
    if (!user) return;

    const unreadNotifs = notifications.filter(n => !n.is_read);
    
    // Mark personal notifications as read
    const personalUnread = unreadNotifs.filter(n => !n.is_broadcast);
    if (personalUnread.length > 0) {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);
    }

    // Insert reads for broadcasts
    const broadcastUnread = unreadNotifs.filter(n => n.is_broadcast);
    if (broadcastUnread.length > 0) {
      const reads = broadcastUnread.map(n => ({
        user_id: user.id,
        notification_id: n.id
      }));
      await supabase
        .from('notification_reads')
        .upsert(reads, { onConflict: 'user_id,notification_id' });
    }

    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  }, [user, notifications]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications
  };
}
